import React from 'react';
import { createBrowserRouter, createRoutesFromElements, RouterProvider, Route, Navigate, Outlet } from 'react-router-dom';
import { DT } from './theme/designTokens';
import { useAuth } from './context/AuthContext';
import { FriendProvider } from './context/FriendContext';
import { AppShell } from './components/shell/AppShell';
import { PublicOnly, RequireAuth, SignupStep1Guard, SignupStep2Guard, AuthAwareFallback } from './routing/authGuards';
import { ComingSoon } from './screens/ComingSoonScreen';

// ── Screens ───────────────────────────────────────────────────────────────────
import { LandingScreen } from './screens/LandingScreen';
import { GetStartedScreen } from './screens/GetStartedScreen';

// Auth (mobile-origin — no shell, centered content)
import { LoginScreen } from './screens/LoginScreen';
import {
  SignupCredentialsScreen,
  SignupUsernameScreen,
} from './screens/SignupScreen';
import {
  ForgotPasswordScreen,
  ForgotPasswordSentScreen,
  ForgotPasswordNewScreen,
  ForgotPasswordSuccessScreen,
} from './screens/ForgotPasswordScreen';

// App screens
import { HomeDS2Screen } from './screens/HomeDS2Screen';
import { LeaderboardScreen } from './screens/LeaderboardScreen';
import { FriendsDS2Screen } from './screens/FriendsDS2Screen';
import { MessagesScreen } from './screens/messages/MessagesScreen';
import { GameScreen } from './screens/GameScreen';
import { LobbyScreen } from './screens/LobbyScreen';
import { PlayOnlineScreen } from './screens/PlayOnlineScreen';
import { PlayAIScreen } from './screens/PlayAIScreen';
import { StatsScreen } from './screens/StatsScreen';
import { AchievementsDS2Screen } from './screens/AchievementsDS2Screen';
import { SettingsDS2Screen } from './screens/SettingsDS2Screen';
import { RulesScreen } from './screens/RulesScreen';
import { DeleteAccountInfoScreen } from './screens/DeleteAccountInfoScreen';
import { TutorialScreen } from './screens/TutorialScreen';

// Admin + dev
import { AgentCommandCenterScreen } from './screens/admin/AgentCommandCenter/AgentCommandCenterScreen';
import { AdminPresenceScreen } from './screens/admin/AdminPresenceScreen';
import { DevGameOverScreen } from './screens/DevGameOverScreen';

// ── Guards ────────────────────────────────────────────────────────────────────
// PublicOnly / RequireAuth / SignupStep1Guard / SignupStep2Guard / AuthAwareFallback
// now live in ./routing/authGuards (unit-tested there without pulling in this
// file's full screen graph).

// ComingSoon moved to ./screens/ComingSoonScreen (unchanged) so tests can
// render it without importing this file's full screen graph.

// ── App ───────────────────────────────────────────────────────────────────────

// Auth-loading gate as a root layout route — renders inside AuthProvider (which
// wraps the router in main.tsx) and gates every route exactly as before.
function AuthGate() {
  const { loading } = useAuth();

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#1A1A1A' }}>
        <div
          style={{
            width:        32,
            height:       32,
            borderRadius: '50%',
            border:       '2px solid rgba(255,255,255,0.10)',
            borderTopColor: '#c9a84c',
            animation:    'spin 0.8s linear infinite',
          }}
        />
      </div>
    );
  }

  return <Outlet />;
}

// Data router (createBrowserRouter) — REQUIRED for route-level navigation
// blocking via useBlocker (see useLeaveGameGuard). The declarative <Routes>
// tree below is unchanged; it is just hosted by a data router now so that
// leaving /game/:id goes through ONE decision for every navigation surface
// (sidebar, mobile drawer, top bar, logo, browser back, programmatic nav).
//
// Exported so native-only startup code that runs OUTSIDE React (see
// utils/nativePushDeepLink.ts, called from main.tsx before the first render)
// can imperatively navigate — e.g. a notification tap arriving before any
// screen has mounted. `router.navigate()` goes through the SAME data-router
// navigation path as every in-app <Link>/useNavigate() call, so it is still
// subject to useLeaveGameGuard's blocker like everything else in the list
// above.
export const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<AuthGate />}>
      {/* ── Standalone — no shell ───────────────────────────────────────── */}
      <Route path="/studio" element={<AgentCommandCenterScreen />} />
      <Route path="/ceo"    element={<Navigate to="/studio" replace />} />
      <Route path="/admin/presence" element={<RequireAuth><AdminPresenceScreen /></RequireAuth>} />
      {/* Interactive tutorial — deliberately PUBLIC and OUTSIDE AppShell:
          scripted gameplay with no session, so no auth requirement, and no
          social chrome (friend channel, invitation/social toasts, active-game
          banner) may run over it. Reachable by direct URL only for the
          vertical slice — the product entry point is a deferred decision. */}
      <Route path="/tutorial" element={<TutorialScreen />} />
      {import.meta.env.DEV && <Route path="/dev/game-over" element={<DevGameOverScreen />} />}

      {/* ── AppShell — persistent top nav + mobile drawer ───────────────── */}
      <Route element={<FriendProvider><AppShell /></FriendProvider>}>

        {/* Landing / auth */}
        <Route path="/"                         element={<PublicOnly><LandingScreen /></PublicOnly>} />
        <Route path="/login"                    element={<PublicOnly><LoginScreen /></PublicOnly>} />
        <Route path="/signup"                   element={<PublicOnly><GetStartedScreen /></PublicOnly>} />
        {/* Email registration — 2 steps (Figma "Register with email 1/2"),
            replacing the old 4-step name/username/email/password/welcome
            wizard. Guards are NOT plain PublicOnly: an incomplete-
            registration user IS logged in (Firebase account already
            exists) and must resume at Step 2, not get bounced to /home. */}
        <Route path="/signup/email"             element={<SignupStep1Guard><SignupCredentialsScreen /></SignupStep1Guard>} />
        <Route path="/signup/username"          element={<SignupStep2Guard><SignupUsernameScreen /></SignupStep2Guard>} />
        <Route path="/forgot-password"          element={<PublicOnly><ForgotPasswordScreen /></PublicOnly>} />
        <Route path="/forgot-password/sent"     element={<PublicOnly><ForgotPasswordSentScreen /></PublicOnly>} />
        {/* Old fake OTP step, never wired to anything real — kept as a safe
            redirect for any stale links/bookmarks rather than 404ing. */}
        <Route path="/forgot-password/code"     element={<Navigate to="/forgot-password" replace />} />
        <Route path="/forgot-password/new-password" element={<PublicOnly><ForgotPasswordNewScreen /></PublicOnly>} />
        <Route path="/forgot-password/success"  element={<PublicOnly><ForgotPasswordSuccessScreen /></PublicOnly>} />

        {/* App */}
        <Route path="/home"         element={<RequireAuth><HomeDS2Screen /></RequireAuth>} />
        <Route path="/play/online"  element={<RequireAuth><PlayOnlineScreen /></RequireAuth>} />
        <Route path="/play/ai"      element={<RequireAuth><PlayAIScreen /></RequireAuth>} />
        <Route path="/friends"      element={<RequireAuth><FriendsDS2Screen /></RequireAuth>} />
        <Route path="/messages"     element={<RequireAuth><MessagesScreen /></RequireAuth>} />
        <Route path="/messages/:friendId" element={<RequireAuth><MessagesScreen /></RequireAuth>} />
        {/* Profile and Settings are ONE canonical experience at /settings
            (Settings V1). /profile is kept only as a redirect for old
            bookmarks/legacy links — auth is enforced by /settings itself. */}
        <Route path="/profile"      element={<Navigate to="/settings" replace />} />
        <Route path="/stats"        element={<RequireAuth><StatsScreen /></RequireAuth>} />
        <Route path="/settings"     element={<RequireAuth><SettingsDS2Screen /></RequireAuth>} />
        <Route path="/shop"         element={<RequireAuth><ComingSoon titleKey="screen.shop" /></RequireAuth>} />
        <Route path="/leaderboard"  element={<RequireAuth><LeaderboardScreen /></RequireAuth>} />
        <Route path="/achievements" element={<RequireAuth><AchievementsDS2Screen /></RequireAuth>} />
        {/* Rules is deliberately PUBLIC (no guard): the marketing footer links
            here for logged-out visitors, and AppShell's chrome already adapts
            to auth state — one route serves both audiences. */}
        <Route path="/rules"        element={<RulesScreen />} />

        {/* Account Deletion info (App Store 5.1.1(v) / Google Play policy):
            deliberately PUBLIC — must be reachable without installing the
            app or logging in. Linked from Footer.tsx's Support column. */}
        <Route path="/delete-account" element={<DeleteAccountInfoScreen />} />

        {/* Lobby — within shell so nav is visible */}
        <Route path="/lobby/:id"    element={<RequireAuth><LobbyScreen /></RequireAuth>} />

        {/* Game — inside AppShell so mobile gets the same TopNav + Drawer as every other screen */}
        <Route path="/game/:id"     element={<RequireAuth><GameScreen /></RequireAuth>} />

        {/* Friend-spectating (read-only) — same screen/shell as /game/:id, just
            with GameScreen's spectate prop set. See GameScreen.tsx's
            GameScreenProps doc comment and network/gameTransport.ts
            createSpectatorGameTransport. */}
        <Route path="/spectate/:id" element={<RequireAuth><GameScreen spectate /></RequireAuth>} />

      </Route>

      <Route path="*" element={<AuthAwareFallback />} />
    </Route>,
  ),
);

export default function App() {
  return <RouterProvider router={router} />;
}
